// Table pagination. Drop-in replacement for components/Pagination.
// Props: currentPage, pageSize, tableCount (rows on this page), totalCount, onPageChange(page),
// onPageSizeChange(event), multipleOption (adds 250–2000 page sizes), siblingCount.
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DOTS, UsePagination } from "hooks/UsePagination";
import { cn } from "./cn";

export function Pagination({ currentPage, pageSize, tableCount, totalCount, onPageChange, onPageSizeChange, multipleOption = false, siblingCount = 1 }) {
  const page = Number(currentPage) || 1;
  const size = Number(pageSize) || 1;
  const total = Number(totalCount) || 0;
  const rows = Number(tableCount) || 0;
  const range = UsePagination({ currentPage: page, totalCount: total, siblingCount, pageSize: size }) || [];
  const last = range[range.length - 1];
  const from = total === 0 ? 0 : (page - 1) * size + 1;
  const to = total === 0 ? 0 : Math.min((page - 1) * size + rows, total);
  const sizes = [20, 50, 100, 150, ...(multipleOption ? [250, 500, 1000, 2000, 5000] : [])];

  const nav = "inline-flex h-8 min-w-8 items-center justify-center gap-1 rounded-lg border border-line bg-panel px-2 text-[12.5px] text-text shadow-card hover:bg-[#f9f9fa] disabled:pointer-events-none disabled:opacity-45";

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-4 py-3 text-[12.5px] text-muted">
      <div className="flex items-center gap-2">
        <span>Rows</span>
        <select value={String(size)} onChange={onPageSizeChange} className="h-8 rounded-lg border border-line bg-panel px-2 text-[12.5px] text-text outline-none focus:border-accent">
          {sizes.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <span className="tabular-nums">
          {from}–{to} of {total.toLocaleString()}
        </span>
      </div>
      <nav aria-label="Pagination" className="flex items-center gap-1">
        <button type="button" className={nav} disabled={page === 1} onClick={() => onPageChange(page - 1)} aria-label="Previous page">
          <ChevronLeft className="size-4" />
        </button>
        {range.map((p, i) =>
          p === DOTS ? (
            <span key={`d${i}`} className="px-1 text-faint">
              …
            </span>
          ) : (
            <button
              key={p}
              type="button"
              onClick={() => onPageChange(p)}
              aria-current={p === page ? "page" : undefined}
              className={cn(nav, "tabular-nums", p === page && "border-accent bg-accent text-white hover:bg-accent")}
            >
              {p}
            </button>
          ),
        )}
        <button type="button" className={nav} disabled={page === last || total === 0} onClick={() => onPageChange(page + 1)} aria-label="Next page">
          <ChevronRight className="size-4" />
        </button>
      </nav>
    </div>
  );
}

export default Pagination;
